import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  SxProps,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { requestOtp, verifyOtpCheck } from "../api/requests/signIn";
import { getItem } from "../utils/utils";
import { colors } from "../utils/constants";
import { useAuth } from "../context/AuthContext";
import { useAlert } from "../context/AlertContext";
import ProfileBadge from "../assets/AppLogo1.png";
import PoliceAppBg from "../assets/PoliceAppBg.svg";
import { labelStyles } from "./createUser";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { login } = useAuth();
  const [loader, setLoader] = useState<boolean>(false);
  const [mobileNo, setMobileNo] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [methodClicked, setMethodClicked] = useState<string>("");
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendDisabled, setResendDisabled] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [publicIp, setPublicIp] = useState<string>("");

  const getOtpHandler = async () => {
    if (!mobileNo || mobileNo.length !== 10) {
      setErrorMessage("Please enter a valid mobile number.");
      return;
    } else {
      setLoader(true);
      setErrorMessage(null);
      try {
        const response = await requestOtp({
          mobileNumber: mobileNo,
          ipAddress: publicIp,
        });
        if (response && response.status === 200) {
          showAlert("OTP sent successfully!", "success");
          setMethodClicked("mobile_otp");
          setResendDisabled(true);
          setResendTimer(30);
        } else {
          showAlert(response.data, "error");
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : String(error));
      } finally {
        setLoader(false);
      }
    }
  };

  const handleMobileNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const filteredValue = rawValue.replace(/[^0-9-]/g, "");
    setMobileNo(filteredValue);
    setErrorMessage(null);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setErrorMessage(null);
      if (value && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const successfulNavigation = (permissions: any[]) => {
    const hasDashboard = permissions.some(
      (p: { module: string }) => p.module === "Dashboard"
    );
    const hasPolicy = permissions.some(
      (p: { module: string }) => p.module === "Roles"
    );
    const hasUsers = permissions.some(
      (p: { module: string }) => p.module === "Users"
    );
    const hasGroups = permissions.some(
      (p: { module: string }) => p.module === "Group"
    );
    const hasEvents = permissions.some(
      (p: { module: string }) => p.module === "EventTags"
    );
    if (hasDashboard) {
      navigate("/");
    } else if (hasPolicy) {
      navigate("/roles");
    } else if (hasUsers) {
      navigate("/users");
    } else if (hasGroups) {
      navigate("/groups");
    } else if (hasEvents) {
      navigate("/events");
    } else {
      navigate("/");
    }
  };

  const verifyOtp = async () => {
    setLoader(true);
    setErrorMessage(null);
    const otpValue = otp.join("");
    if (otpValue.length !== 6 || !/^[0-9]{6}$/.test(otpValue)) {
      setErrorMessage("Please enter a valid 6-digit OTP.");
      setLoader(false);
      return;
    }
    try {
      const response = await verifyOtpCheck({
        mobileNumber: mobileNo,
        otp: otpValue,
        ipAddress: publicIp,
      });
      if (response && response.status === 200) {
        login({
          user: response.data.user,
          token: response.data.token,
        });
        if (response?.data?.user?.permissions) {
          const permissions = response.data.user.permissions;
          await successfulNavigation(permissions);
        }
      } else {
        setMethodClicked("mobile_otp");
        setErrorMessage("OTP verification failed.");
      }
    } catch {
      setErrorMessage("OTP verification failed");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const token = getItem("token");
    if (token) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (resendDisabled && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setResendDisabled(false);
            clearInterval(timer!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendDisabled, resendTimer]);

  useEffect(() => {
    const fetchPublicIp = async () => {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        setPublicIp(data.ip || "");
      } catch (error) {
        console.error("Failed to fetch public IP:", error);
        setPublicIp("");
      }
    };
    fetchPublicIp();
  }, []);

  return (
    <Box sx={mainWrapperSx}>
      <Box sx={cardsWrapperSx}>
        <Typography sx={titleSx}>Intelligence Department</Typography>
        <Typography sx={titleSx}>Telangana</Typography>
        <Box style={{ maxWidth: 200, maxHeight: 200 }}>
          <img
            src={ProfileBadge}
            alt="Police Badge"
            style={{ width: "100%", height: "auto" }}
          />
        </Box>
        <Box sx={subTitleSx}>
          <Typography sx={{ fontWeight: 600, fontSize: "20px" }}>
            Welcome Back!
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#515151" }}>
            Log in to existing police account
          </Typography>
        </Box>
        {methodClicked === "" ? (
          <>
            <TextField
              fullWidth
              name="mobileNo"
              placeholder="Enter your mobile number"
              onChange={handleMobileNoChange}
              value={mobileNo}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff",
                },
              }}
              inputProps={{ maxLength: 10, minLength: 10 }}
              slotProps={{
                input: {
                  sx: labelStyles,
                },
              }}
            />

            {errorMessage && (
              <Typography color="error" sx={{ mb: 2, fontSize: 13 }}>
                {errorMessage}
              </Typography>
            )}
            <Button
              onClick={getOtpHandler}
              fullWidth
              variant="contained"
              sx={buttonSx}
              disabled={loader}
              loading={loader}
            >
              Send OTP
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{ fontWeight: 600 }}>OTP Verification</Typography>

            <Typography sx={{ fontSize: 14, color: "#555" }}>
              Enter OTP number verification we’ve sent to your mobile number
              associated with your account.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (otpRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  slotProps={{
                    input: {
                      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                        handleOtpKeyDown(index, e),
                    },
                  }}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: "center", fontSize: 18, padding: 8 },
                  }}
                  sx={{
                    width: 40,
                    borderRadius: "50%",
                    "& .MuiInputBase-root": {
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                    },
                  }}
                />
              ))}
            </Box>
            {errorMessage && (
              <Typography color="error" sx={{ mb: 2, fontSize: 13 }}>
                {errorMessage}
              </Typography>
            )}
            <Button
              onClick={verifyOtp}
              fullWidth
              variant="contained"
              sx={buttonSx}
              disabled={loader}
            >
              {loader ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Continue Login"
              )}
            </Button>
          </>
        )}

        <Typography sx={{ mt: 4, fontSize: 13, color: "#888" }}>
          Ensuring state security with real-time intelligence
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;

const mainWrapperSx: SxProps = {
  height: "100vh",
  width: "100vw",
  backgroundImage: `url(${PoliceAppBg})`,
  display: "flex",
  justifyContent: "end",
  alignItems: "center",
  overflow: "hidden",
};

const cardsWrapperSx: SxProps = {
  height: { xs: "auto", sm: "auto", md: "auto" },
  minHeight: { xs: "150px", sm: "200px", md: "300px" },
  maxHeight: { xs: "80vh", sm: "85vh", md: "80vh" },
  maxWidth: { xs: "90%", sm: "70%", md: "40%", lg: "30%" },
  width: "100%",
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  padding: { xs: "20px", sm: "32px", md: "48px" },
  mr: { xs: 2, sm: 2, md: 4 },
  display: "flex",
  flexDirection: "column",
  justifyContent: { xs: "flex-start", sm: "center", md: "flex-start" },
  alignItems: "center",
  gap: { xs: 1, sm: "1.5", md: 2 },
  overflow: "auto",
  boxSizing: "border-box",
  "@media (max-height: 600px)": {
    padding: "24px 16px",
    minHeight: "120px",
    maxHeight: "90vh",
    justifyContent: "flex-start",
    gap: 0.5,
  },
  "@media (min-height: 900px)": {
    paddingTop: "32px",
    paddingBottom: "32px",
    maxHeight: "80vh",
  },
};

const titleSx: SxProps = {
  fontWeight: 400,
  fontSize: "24px",
  color: "#000000",
  whiteSpace: "nowrap",
};

const subTitleSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.4,
};

const buttonSx: SxProps = {
  backgroundColor: colors.indigoBlueColor,
  color: "#ffffff",
  textTransform: "none",
  fontWeight: 400,
  fontSize: 14,
  borderRadius: "8px",
  height: "48px",
  padding: "8px 12px",
};
